import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CreditCard, Clock, CheckCircle2, LogOut } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { BrandMark } from "../components/Layout";
import { PlanCards, CheckoutSummary, useCheckout } from "../components/PlanPicker";

export function formatPrice(cents, currency = "eur") {
  if (cents == null) return "";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

const STATUS_COPY = {
  expired: "Your 7-day free trial has ended.",
  past_due: "Your last payment didn't go through.",
  canceled: "Your subscription has been canceled.",
  trialing: "You're on your free trial.",
};

export default function Subscribe() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState("standard");
  const checkout = useCheckout();

  useEffect(() => {
    api.get("/billing/status")
      .then(({ data }) => setBilling(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-500"><Loader2 className="animate-spin" /></div>;

  const b = billing || {};
  const canManage = !!b.can_manage;
  const allowed = b.allowed === true;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="subscribe-page">
      <div className="surface-dark px-5 py-4 flex items-center justify-between border-b border-zinc-800/60">
        <div className="flex items-center gap-2.5"><BrandMark company={null} /></div>
        <button onClick={logout} data-testid="subscribe-logout-btn" className="text-zinc-300 hover:text-white flex items-center gap-1.5 text-sm font-semibold"><LogOut size={16} /> {t("Log out")}</button>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-2 text-[#F26722] font-bold text-sm">
            <Clock size={16} /> {t(STATUS_COPY[b.status] || "Subscription required")}
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#1A1A1A] mt-3">
            {allowed ? t("You're all set") : t("Continue with RV Service Tool")}
          </h1>

          {allowed ? (
            <>
              <p className="text-zinc-500 mt-3">{t("Your access is active. Head back to your reports.")}</p>
              <button onClick={() => nav("/reports")} data-testid="subscribe-continue-btn"
                className="w-full bg-[#F26722] text-white font-bold py-3.5 rounded-xl mt-6 hover:bg-[#D95513] transition-colors flex items-center justify-center gap-2">
                <CheckCircle2 size={18} /> {t("Go to app")}
              </button>
            </>
          ) : canManage && b.payments_configured !== false ? (
            <>
              <p className="text-zinc-500 mt-3">{t("Choose one plan for your whole company.")}</p>
              <div className="mt-6"><PlanCards billing={b} value={plan} onChange={setPlan} /></div>
              <CheckoutSummary billing={b} plan={plan} />
              <button onClick={() => checkout.start(plan)} disabled={checkout.loading} data-testid="subscribe-checkout-btn"
                className="w-full bg-[#F26722] text-white font-bold py-3.5 rounded-xl mt-6 hover:bg-[#D95513] disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                {checkout.loading ? <Loader2 className="animate-spin" size={18} /> : <><CreditCard size={18} /> {t("Subscribe")}</>}
              </button>
              <p className="text-center text-xs text-zinc-500 mt-3">{t("Secure payment via Stripe. Cancel anytime.")}</p>
            </>
          ) : canManage && b.payments_configured === false ? (
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 mt-6" data-testid="subscribe-not-configured">
              <p className="text-zinc-600">
                {t("Online payments are not switched on yet. Please check back soon, or contact us at +31650825998.")}
              </p>
            </div>
          ) : (
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 mt-6" data-testid="subscribe-ask-manager">
              <p className="text-zinc-600">
                {t("Your trial has ended — your manager handles billing. Please ask your manager to renew the subscription.")}
              </p>
            </div>
          )}

          <p className="text-center text-sm text-zinc-500 mt-8">{t("Signed in as")} {user?.name} {user?.company_name ? `· ${user.company_name}` : ""}</p>
        </div>
      </div>
    </div>
  );
}
